import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { VisualCalendar, CalendarSyncButton } from "@/components/calendar";
import { AvailabilitySettings } from "@/components/calendar/AvailabilitySettings";
import { 
  Calendar, Video, Clock, User, Plus, ExternalLink, RefreshCw, CheckCircle
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface Meeting {
  id: string;
  clientId: string;
  clientName: string;
  mandateId?: string;
  title: string;
  description?: string;
  scheduledAt: any;
  duration: number;
  meetingLink: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  attendees: string[];
}

const mockMeetings: Meeting[] = [
  {
    id: "mock-meeting-1",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
    mandateId: "mock-mandate-1",
    title: "Revue Due Diligence - Point Hebdomadaire",
    description: "Discussion sur l'avancement de l'analyse juridique",
    scheduledAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    duration: 60,
    meetingLink: "https://meet.example.com/abc123",
    status: "scheduled",
    attendees: ["jp.martin@example.com", "consultant@example.com"],
  },
  {
    id: "mock-meeting-2",
    clientId: "mock-client-2",
    clientName: "Sophie Tremblay",
    mandateId: "mock-mandate-2",
    title: "Présentation Stratégie Fiscale",
    scheduledAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    duration: 90,
    meetingLink: "https://meet.example.com/def456",
    status: "scheduled",
    attendees: ["s.tremblay@example.com", "consultant@example.com"],
  },
  {
    id: "mock-meeting-3",
    clientId: "mock-client-3",
    clientName: "Marc Dubois",
    title: "Nouveau Mandat - Discussion Initiale",
    scheduledAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    duration: 45,
    meetingLink: "https://meet.example.com/ghi789",
    status: "scheduled",
    attendees: ["m.dubois@broker.com", "consultant@example.com"],
  },
  {
    id: "mock-meeting-4",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
    title: "Suivi Immigration",
    scheduledAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    duration: 30,
    meetingLink: "https://meet.example.com/jkl012",
    status: "completed",
    attendees: ["jp.martin@example.com", "consultant@example.com"],
  },
];

const translations = {
  en: {
    title: "Calendar",
    subtitle: "Manage your schedule, meetings, and availability",
    newMeeting: "New Meeting",
    syncCalendar: "Sync Calendar",
    calendarView: "Calendar View",
    upcomingMeetings: "Upcoming Meetings",
    availability: "Availability",
    noUpcomingMeetings: "No upcoming meetings",
    scheduledMeetings: "scheduled meetings",
    joinMeeting: "Join Meeting",
    viewDetails: "View Details",
    duration: "Duration",
    minutes: "min",
    today: "Today",
    tomorrow: "Tomorrow",
    loading: "Loading calendar...",
    completed: "Completed",
    scheduled: "Scheduled",
    cancelled: "Cancelled",
    rescheduled: "Rescheduled",
  },
  fr: {
    title: "Calendrier",
    subtitle: "Gérez votre emploi du temps, réunions et disponibilités",
    newMeeting: "Nouvelle Réunion",
    syncCalendar: "Synchroniser",
    calendarView: "Vue Calendrier",
    upcomingMeetings: "Réunions à Venir",
    availability: "Disponibilités",
    noUpcomingMeetings: "Aucune réunion à venir",
    scheduledMeetings: "réunions planifiées",
    joinMeeting: "Rejoindre",
    viewDetails: "Voir Détails",
    duration: "Durée",
    minutes: "min",
    today: "Aujourd'hui",
    tomorrow: "Demain",
    loading: "Chargement du calendrier...",
    completed: "Terminée",
    scheduled: "Planifiée",
    cancelled: "Annulée",
    rescheduled: "Reportée",
  },
};

export default function ConsultantCalendar() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("calendar");

  useEffect(() => {
    if (user) {
      loadMeetings();
    }
  }, [user]);

  const loadMeetings = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const meetingsRef = collection(db, "consultantMeetings");
      const meetingsQuery = query(
        meetingsRef,
        where("consultantId", "==", user.email),
        orderBy("scheduledAt", "desc")
      );
      const meetingsSnapshot = await getDocs(meetingsQuery);
      const meetingsData = meetingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Meeting[];
      setMeetings(meetingsData.length > 0 ? meetingsData : mockMeetings);
    } catch (error) {
      console.error("Error loading meetings:", error);
      setMeetings(mockMeetings);
    } finally {
      setIsLoading(false);
    }
  };

  const upcomingMeetings = meetings.filter(m => {
    const meetingDate = m.scheduledAt instanceof Date ? m.scheduledAt : m.scheduledAt?.toDate?.() || new Date(m.scheduledAt);
    return m.status === 'scheduled' && meetingDate > new Date();
  }).sort((a, b) => {
    const dateA = a.scheduledAt instanceof Date ? a.scheduledAt : a.scheduledAt?.toDate?.() || new Date(a.scheduledAt);
    const dateB = b.scheduledAt instanceof Date ? b.scheduledAt : b.scheduledAt?.toDate?.() || new Date(b.scheduledAt);
    return dateA.getTime() - dateB.getTime();
  });

  const calendarEvents = meetings.map(m => {
    const start = m.scheduledAt instanceof Date ? m.scheduledAt : m.scheduledAt?.toDate?.() || new Date(m.scheduledAt);
    const end = new Date(start.getTime() + m.duration * 60000);
    return {
      id: m.id,
      title: m.title,
      start,
      end,
      type: 'buyer_seller' as const,
      status: m.status === 'scheduled' ? 'confirmed' as const : m.status === 'completed' ? 'completed' as const : 'cancelled' as const,
    };
  });

  const formatMeetingDate = (date: any) => {
    const d = date instanceof Date ? date : date?.toDate?.() || new Date(date);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (d.toDateString() === now.toDateString()) {
      return t.today;
    } else if (d.toDateString() === tomorrow.toDateString()) {
      return t.tomorrow;
    }
    return format(d, 'EEEE, d MMMM', { locale: language === 'fr' ? fr : undefined });
  };

  const formatMeetingTime = (date: any) => {
    const d = date instanceof Date ? date : date?.toDate?.() || new Date(date);
    return format(d, 'HH:mm');
  };

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case 'scheduled': return 'default';
      case 'completed': return 'secondary';
      case 'cancelled': return 'destructive';
      case 'rescheduled': return 'outline';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <CalendarSyncButton />
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            {t.newMeeting}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calendar">{t.calendarView}</TabsTrigger>
          <TabsTrigger value="meetings">{t.upcomingMeetings}</TabsTrigger>
          <TabsTrigger value="availability">{t.availability}</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <VisualCalendar meetings={calendarEvents as any} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meetings" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {upcomingMeetings.length} {t.scheduledMeetings}
              </p>
            </div>

            {upcomingMeetings.length > 0 ? (
              <div className="space-y-4">
                {upcomingMeetings.map((meeting) => (
                  <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={getStatusBadgeVariant(meeting.status)}>
                              {t[meeting.status as keyof typeof t] || meeting.status}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {formatMeetingDate(meeting.scheduledAt)}
                            </span>
                          </div>
                          <CardTitle className="text-lg">{meeting.title}</CardTitle>
                          {meeting.description && (
                            <CardDescription className="mt-1">{meeting.description}</CardDescription>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Client</p>
                            <p className="text-sm font-medium">{meeting.clientName}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">{t.duration}</p>
                            <p className="text-sm font-medium">{formatMeetingTime(meeting.scheduledAt)} • {meeting.duration} {t.minutes}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Participants</p>
                            <p className="text-sm font-medium">{meeting.attendees.length}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button size="sm" className="flex-1" asChild>
                          <a href={meeting.meetingLink} target="_blank" rel="noopener noreferrer">
                            <Video className="h-4 w-4 mr-1" />
                            {t.joinMeeting}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1">
                          {t.viewDetails}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">{t.noUpcomingMeetings}</h3>
                  <p className="text-muted-foreground text-center mt-2 mb-4">
                    {language === 'fr' 
                      ? 'Planifiez une réunion avec vos clients pour commencer'
                      : 'Schedule a meeting with your clients to get started'}
                  </p>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    {t.newMeeting}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="availability" className="mt-6">
          <AvailabilitySettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
